import React, { useCallback, useMemo } from 'react'
import { Box, Button, Group, Text } from '@mantine/core'
import { DataTable, DataTableSortStatus } from 'mantine-datatable'
import { DownloadSimpleIcon } from '@phosphor-icons/react'
import type { Column, ServerSortState, ColumnFilterEntry } from '../types/analysisList'
import { COLUMN_WIDTH_MIN, estimateColumnWidth } from '../utils/columnWidth'
import {
  ColumnFilter,
  DEFAULT_FILTER_BY_TYPE,
  Filter,
  FilterDataType,
  isFilterActive,
} from './ColumnFilter'
import { TablePagination } from './TablePagination'

const filterDataTypeFor = (cellDataType: string): FilterDataType =>
  cellDataType === 'integer' ? 'number' : 'string'

// セル表示: null/空はハイフン、それ以外はサーバが返した値をそのまま表示
function displayCell(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value.length > 0 ? value : '-'
  return String(value)
}

// 日付セルは YYYY/MM/DD 形式に整形する
function displayDateCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-'
  const d = new Date(value as string | number | Date)
  if (Number.isNaN(d.getTime())) return displayCell(value)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}

type Row = Record<string, unknown>

interface Props {
  combinedRows: Row[]
  sliceOffset: number
  columns: Column[]
  page: number
  onChangePage: (page: number) => void
  perPage: number
  onChangePerPage: (perPage: number) => void
  totalCount: number
  sortState: ServerSortState
  onChangeSort: (next: ServerSortState) => void
  columnFilters: Record<string, ColumnFilterEntry>
  onChangeColumnFilters: (
    updater: (prev: Record<string, ColumnFilterEntry>) => Record<string, ColumnFilterEntry>,
  ) => void
  onExportCsv: () => void
  isExporting?: boolean
  isLoading?: boolean
  csvError?: string | null
  // CSVダウンロードボタンの左に並べる追加ボタン(画面固有のCSV出力など)
  extraActions?: React.ReactNode
}

export const AnalysisListTable: React.FC<Props> = ({
  combinedRows,
  sliceOffset,
  columns,
  page,
  onChangePage,
  perPage,
  onChangePerPage,
  totalCount,
  sortState,
  onChangeSort,
  columnFilters,
  onChangeColumnFilters,
  onExportCsv,
  isExporting,
  isLoading,
  csvError,
  extraActions,
}) => {
  const updateColumnFilter = useCallback(
    (field: string, dataType: FilterDataType, filter: Filter) => {
      onChangeColumnFilters((prev) => ({
        ...prev,
        [field]: { filter, dataType },
      }))
    },
    [onChangeColumnFilters],
  )
  const columnWidths = useMemo<Record<string, number>>(() => {
    const result: Record<string, number> = {}
    const sampleRows = combinedRows.slice(0, 20)
    for (const col of columns) {
      const samples = sampleRows.map((r) => r[col.field])
      result[col.field] = estimateColumnWidth(col.headerName, samples)
    }
    return result
  }, [combinedRows, columns])

  // DataTable のカラム定義
  const tableColumns = useMemo(
    () =>
      columns.map((col) => {
        // 'number' は小数を含む数値列(キャンペーンポイント等)。整数列と同じく右寄せにする
        const isNumber = col.cellDataType === 'integer' || col.cellDataType === 'number'
        const dataType = filterDataTypeFor(col.cellDataType)
        const currentFilter = columnFilters[col.field]?.filter ?? DEFAULT_FILTER_BY_TYPE[dataType]
        const filterable = col.filterable !== false
        return {
          accessor: col.field,
          title: col.headerName,
          sortable: filterable,
          resizable: true,
          ellipsis: true,
          width: columnWidths[col.field] ?? COLUMN_WIDTH_MIN,
          titleStyle: isNumber ? (): React.CSSProperties => ({ textAlign: 'right' }) : undefined,
          cellsStyle: isNumber ? (): React.CSSProperties => ({ textAlign: 'right' }) : undefined,
          render: (r: Row) =>
            col.cellDataType === 'date'
              ? displayDateCell(r[col.field])
              : displayCell(r[col.field]),
          filter: filterable ? (
            <ColumnFilter
              filter={currentFilter}
              onChange={(f) => updateColumnFilter(col.field, dataType, f)}
              dataType={dataType}
            />
          ) : undefined,
          filtering: filterable && isFilterActive(columnFilters[col.field]?.filter),
        }
      }),
    [columns, columnWidths, columnFilters, updateColumnFilter],
  )

  // ページネーション制御
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))
  const displayRows = combinedRows.slice(sliceOffset, sliceOffset + perPage)

  // 行数が少ないときにテーブル下部の余白が出ないよう、内部スクロールが必要な件数のときだけ
  // 高さを固定する。0件時は「データがありません」の表示領域を確保する。
  // （maxHeight は内部の ScrollArea が flex アイテムで縮まないため機能しない）
  const tableHeight = displayRows.length === 0 ? 400 : displayRows.length >= 15 ? 600 : undefined

  return (
    <>
      <Box
        style={{
          border: '1px solid #0000001A',
          borderRadius: '8px',
          overflow: 'hidden',
          flex: 1,
          minWidth: 0,
          width: '100%',
        }}
        mt={16}
      >
        <DataTable
          withColumnBorders
          fetching={isLoading}
          records={displayRows}
          noRecordsText="データがありません"
          height={tableHeight}
          styles={{
            root: { border: 'none' },
            header: { backgroundColor: '#F8F9FA' },
          }}
          columns={tableColumns}
          sortStatus={
            sortState
              ? { columnAccessor: sortState.field, direction: sortState.direction }
              : { columnAccessor: '', direction: 'asc' }
          }
          onSortStatusChange={(s: DataTableSortStatus<Row>) => {
            if (!s.columnAccessor) {
              onChangeSort(null)
            } else {
              onChangeSort({ field: s.columnAccessor, direction: s.direction })
            }
          }}
        />

        <TablePagination
          page={page}
          onChangePage={onChangePage}
          perPage={perPage}
          onChangePerPage={onChangePerPage}
          totalPages={totalPages}
          isEmpty={displayRows.length === 0}
        />
      </Box>

      <Group mt={16} justify="end">
        {extraActions}
        <Button
          variant="outline"
          radius="xl"
          onClick={onExportCsv}
          disabled={isExporting}
          leftSection={<DownloadSimpleIcon size={16} weight="bold" />}
        >
          {isExporting ? 'ダウンロード中' : 'CSVダウンロード'}
        </Button>
      </Group>
      {csvError && (
        <Group mt={4} justify="end">
          <Text c="red" fz={13}>{csvError}</Text>
        </Group>
      )}
    </>
  )
}
